import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import { AlertTriangle, Copy, Lock, Unlock, Download, Loader2, Package, Check, X, ChevronDown, ChevronRight } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/** Bill validation workspace: what was excluded, package types seen, duplicate-order
 *  review with credit decisions, plus month lock / unlock and the Excel export. */
export default function AdminBillReview({ accountId, month, preview, onReload }) {
  const s = preview?.summary;
  const [y, m] = month.split("-");
  const [decisions, setDecisions] = useState({});
  const [lock, setLock] = useState(preview?.lock || { locked: false });
  const [busy, setBusy] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);

  useEffect(() => { setLock(preview?.lock || { locked: false }); }, [preview]);

  const loadDecisions = async () => {
    try {
      const { data } = await api.get(`/admin/accounts/${accountId}/billing/dup-decisions?year=${y}&month=${m}`);
      const map = {};
      (data.decisions || []).forEach((d) => { map[d.order_number] = d; });
      setDecisions(map);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { if (accountId) loadDecisions(); /* eslint-disable-next-line */ }, [accountId, month]);

  const decide = async (orderNumber, decision, amount, basis) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/accounts/${accountId}/billing/dup-decisions?year=${y}&month=${m}`, {
        order_number: orderNumber, decision, amount: Number(amount || 0), basis: basis || null,
      });
      setDecisions((d) => ({ ...d, [orderNumber]: data.decision }));
      toast.success(decision === "credited" ? `Credited ${formatCurrency(Number(amount))} for order ${orderNumber}` : `Marked order ${orderNumber} as no credit`);
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doLock = async () => {
    const note = window.prompt("Optional note for the lock audit trail (e.g. “Approved with Sergey, invoice sent”) — leave blank to skip");
    if (note === null) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/accounts/${accountId}/billing/lock?year=${y}&month=${m}`, { note: note.trim() || null });
      toast.success(`${month} locked at ${formatCurrency(data.to_be_billed)}`);
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doUnlock = async () => {
    const reason = window.prompt("Why are you unlocking this month? (recorded in the audit trail)");
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${accountId}/billing/unlock?year=${y}&month=${m}`, { reason: reason.trim() });
      toast.success(`${month} unlocked`);
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!s) return null;
  const locked = !!lock?.locked;

  return (
    <div data-testid="bill-review" className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">Validate the bill</h3>
          <p className="text-xs text-slate-500 mt-0.5">Check what was excluded, review duplicate orders, then lock the month once you&apos;re happy.</p>
        </div>
        <div className="flex items-center gap-2">
          {locked && (
            <span data-testid="bill-lock-pill" className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-900 text-white px-2.5 py-1 rounded-full">
              <Lock className="w-3 h-3" /> Locked {(lock.locked_at || "").slice(0, 10)}
            </span>
          )}
          <a
            data-testid="export-xlsx-btn"
            href={`${BASE}/api/admin/accounts/${accountId}/billing/export.xlsx?year=${y}&month=${m}`}
            className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold px-3.5 py-2 rounded-md text-sm transition-colors"
          >
            <Download className="w-4 h-4" /> Export .xlsx
          </a>
          {locked ? (
            <button data-testid="unlock-month-btn" onClick={doUnlock} disabled={busy}
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold px-3.5 py-2 rounded-md text-sm transition-colors">
              <Unlock className="w-4 h-4" /> Unlock month
            </button>
          ) : (
            <button data-testid="lock-month-btn" onClick={doLock} disabled={busy}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-black disabled:opacity-50 text-white font-semibold px-3.5 py-2 rounded-md text-sm transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Lock month
            </button>
          )}
        </div>
      </div>

      {locked && (
        <p data-testid="bill-lock-note" className="text-xs bg-slate-900/5 border border-slate-200 rounded-md px-3 py-2 text-slate-700">
          This month is frozen at <strong>{formatCurrency(s.to_be_billed)}</strong> — ShipStation changes no longer move it.
          Locked by {lock.locked_by}. A super admin can unlock it to apply a credit the customer needs.
        </p>
      )}

      {s.meta_missing_count > 0 && (
        <p data-testid="meta-gap-note" className="text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-slate-600">
          {s.meta_missing_count} shipment{s.meta_missing_count === 1 ? "" : "s"} could not be matched to a ShipStation order, so their brand tag and package type are blank
          (the order was placed more than 75 days before this month). Fees are unaffected.
        </p>
      )}

      {s.excluded_count > 0 && (
        <div data-testid="excluded-note" className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <button onClick={() => setShowExcluded((v) => !v)} className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            {showExcluded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <AlertTriangle className="w-4 h-4" />
            {s.excluded_count} shipment{s.excluded_count === 1 ? "" : "s"} excluded (outside billing month)
          </button>
          <p className="text-xs text-amber-800 mt-1 ml-6">ShipStation returns a few next-month labels on a month query. They are billed in their own month.</p>
          {showExcluded && (
            <ul className="mt-2 ml-6 text-xs text-amber-900 space-y-0.5" data-testid="excluded-list">
              {(s.excluded_sample || []).map((x) => (
                <li key={`${x.order_number}-${x.ship_date}`}>Order {x.order_number} — shipped {x.ship_date}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Package types seen */}
      {(s.packaging_lines || []).length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-slate-800 flex items-center gap-1.5 mb-2"><Package className="w-4 h-4 text-slate-400" /> Package types this month</h4>
          <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="package-types-table">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="py-2 px-3">Package type</th><th className="py-2 px-3">ShipStation code</th><th className="py-2 px-3 text-right">Shipments</th><th className="py-2 px-3 text-right">Charged</th></tr>
              </thead>
              <tbody>
                {s.packaging_lines.map((p) => (
                  <tr key={p.package_type} data-testid={`pkg-line-${p.package_code || "none"}`} className="border-t border-slate-100">
                    <td className="py-2 px-3 font-medium text-slate-800">{p.package_type}</td>
                    <td className="py-2 px-3"><code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{p.package_code || "—"}</code></td>
                    <td className="py-2 px-3 text-right">{p.count}</td>
                    <td className="py-2 px-3 text-right font-semibold">{formatCurrency(p.charged)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Add a charge for any of these codes in “Packaging charges” above — it applies per shipment.</p>
        </div>
      )}

      {/* Duplicate order review */}
      <div>
        <h4 className="text-sm font-bold text-slate-800 flex items-center gap-1.5 mb-2">
          <Copy className="w-4 h-4 text-slate-400" /> Duplicate order numbers
          <span className="text-xs font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{s.duplicates_count || 0}</span>
        </h4>
        {(s.duplicate_groups || []).length === 0 ? (
          <p className="text-sm text-slate-400">No order number shipped more than once this month.</p>
        ) : (
          <div className="border border-slate-200 rounded-lg max-h-[420px] overflow-y-auto" data-testid="duplicates-table">
            {s.duplicate_groups.map((g) => (
              <DuplicateRow key={g.order_number} group={g} decision={decisions[g.order_number]} locked={locked} busy={busy} onDecide={decide} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DuplicateRow({ group: g, decision, locked, busy, onDecide }) {
  const [amount, setAmount] = useState("");
  const [open, setOpen] = useState(false);
  const decided = !!decision;

  return (
    <div data-testid={`dup-group-${g.order_number}`} className="border-b border-slate-100 last:border-b-0 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <button onClick={() => setOpen((v) => !v)} className="font-semibold text-slate-900 text-sm inline-flex items-center gap-1">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {g.order_number}
        </button>
        <span className="text-xs font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{g.count} shipments</span>
        {g.multi_brand ? (
          <span data-testid={`dup-multibrand-${g.order_number}`} className="text-xs font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Multi-brand sale</span>
        ) : (
          <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Single brand</span>
        )}
        <span className="text-xs text-slate-500 truncate max-w-[380px]">{g.brands.join(" · ") || "no brand tag"}</span>
        <span className="text-sm font-semibold text-slate-800 ml-auto">{formatCurrency(g.total)}</span>
      </div>

      {open && (
        <div className="mt-2 ml-5 border-l-2 border-slate-100 pl-3 space-y-1">
          {g.shipments.map((sh, i) => (
            <div key={`${sh.tracking_number}-${i}`} className="text-xs text-slate-600 flex flex-wrap gap-x-3">
              <span className="text-slate-400">#{i + 1}</span>
              <span>{(sh.ship_date || "").slice(0, 10)}</span>
              <span>{sh.carrier}</span>
              <code className="bg-slate-50 px-1 rounded">{sh.tracking_number}</code>
              <span>{sh.units} units</span>
              <span className="font-semibold">{formatCurrency(sh.total)}</span>
              {sh.brand && <span className="text-slate-400 truncate max-w-[240px]">{sh.brand}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {decided ? (
          <span data-testid={`dup-decision-${g.order_number}`} className={`text-xs font-semibold px-2 py-1 rounded ${decision.decision === "credited" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
            {decision.decision === "credited" ? `Credited ${formatCurrency(decision.amount)}` : "No credit"} · {decision.decided_by}
          </span>
        ) : null}
        {!locked && (
          <>
            <input
              data-testid={`dup-amount-${g.order_number}`}
              type="number" step="0.01" min="0" value={amount} placeholder="$ credit"
              onChange={(e) => setAmount(e.target.value)}
              className="w-24 px-2 py-1 border border-slate-300 rounded text-xs"
            />
            <QuickFill label={`Total ${formatCurrency(g.total)}`} onClick={() => setAmount(String(g.total))} testId={`dup-fill-total-${g.order_number}`} />
            <QuickFill label={`Fulfillment ${formatCurrency(g.fulfillment_total)}`} onClick={() => setAmount(String(g.fulfillment_total))} testId={`dup-fill-ff-${g.order_number}`} />
            <QuickFill label={`Shipping ${formatCurrency(g.shipping_total)}`} onClick={() => setAmount(String(g.shipping_total))} testId={`dup-fill-ship-${g.order_number}`} />
            <button
              data-testid={`dup-credit-${g.order_number}`}
              disabled={busy || !(Number(amount) > 0)}
              onClick={() => onDecide(g.order_number, "credited", amount, "custom")}
              className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-semibold px-2.5 py-1 rounded"
            >
              <Check className="w-3 h-3" /> Apply credit
            </button>
            <button
              data-testid={`dup-nocredit-${g.order_number}`}
              disabled={busy}
              onClick={() => onDecide(g.order_number, "no_credit", 0, null)}
              className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-2.5 py-1 rounded"
            >
              <X className="w-3 h-3" /> No credit
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function QuickFill({ label, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className="text-xs text-blue-600 hover:text-blue-800 underline decoration-dotted">
      {label}
    </button>
  );
}
